import './style/importKeywordsDialog.scss';

import {ResponseData} from '@dvrd/dvr-controls';

import {Icon} from '@iconify/react';

import React, {useCallback, useMemo, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {BluemateDialog, BluemateInput} from '../../controls';

import {bulkImportKeywords} from '../../../util/requestSender';

import {showSnackbar} from '@dvrd/dvr-controls';

type Mode = 'paste' | 'file';

interface ParsedRow {
    ean: string;
    keyword: string;
}

interface Props {
    active: boolean;
    onClose: VoidFunction;
    accountId: string;
    onSuccess: VoidFunction;
}

const EAN_RE = /^\d{8,13}$/;
const MAX_PREVIEW_EANS = 3;

function parseCSVContent(text: string): Array<ParsedRow> {
    const rows: Array<ParsedRow> = [];
    const lines = text.split(/\r?\n/);
    let currentEan: string | null = null;

    for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        // Skip header rows
        if (trimmed.toLowerCase().startsWith('ean')) continue;

        // Check if line contains a separator (EAN;keyword or EAN,keyword format)
        const hasSeparator = trimmed.includes(';') || trimmed.includes(',');
        if (hasSeparator) {
            const separator = trimmed.includes(';') ? ';' : ',';
            const parts = trimmed.split(separator);
            const ean = parts[0].trim();
            const keyword = parts.slice(1).join(separator).trim();
            if (ean && keyword) {
                rows.push({ean, keyword});
                currentEan = ean;
            }
            continue;
        }

        // No separator: line is either an EAN or a keyword for the current EAN
        if (EAN_RE.test(trimmed)) {
            currentEan = trimmed;
        } else if (currentEan) {
            rows.push({ean: currentEan, keyword: trimmed});
        }
    }
    return rows;
}

export default function ImportKeywordsDialog(props: Props) {
    const {t} = useTranslation();
    const {active, onClose, accountId, onSuccess} = props;

    const [mode, setMode] = useState<Mode>('paste');
    const [pasteText, setPasteText] = useState('');
    const [importFile, setImportFile] = useState<File | null>(null);
    const [fileContent, setFileContent] = useState('');
    const [importing, setImporting] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const parsedRows = useMemo(() => {
        const content = mode === 'paste' ? pasteText : fileContent;
        return parseCSVContent(content);
    }, [mode, pasteText, fileContent]);

    const groupedByEan = useMemo(() => {
        const grouped: Record<string, Array<string>> = {};
        for (const row of parsedRows) {
            if (!grouped[row.ean]) grouped[row.ean] = [];
            if (!grouped[row.ean].includes(row.keyword)) {
                grouped[row.ean].push(row.keyword);
            }
        }
        return grouped;
    }, [parsedRows]);

    const eanCount = Object.keys(groupedByEan).length;

    const handleFileChange = useCallback((file: File | null) => {
        setImportFile(file);
        if (file) {
            const reader = new FileReader();
            reader.onload = e => {
                setFileContent((e.target?.result as string) || '');
            };
            reader.readAsText(file);
        } else {
            setFileContent('');
        }
    }, []);

    const handleFileDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            const file = e.dataTransfer.files[0];
            if (file) handleFileChange(file);
        },
        [handleFileChange]
    );

    const handleFileInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            handleFileChange(e.target.files?.[0] ?? null);
        },
        [handleFileChange]
    );

    const handleImport = useCallback(() => {
        if (!eanCount) return;
        const totalKeywords = parsedRows.length;
        setImporting(true);
        setPasteText('');
        setImportFile(null);
        setFileContent('');

        bulkImportKeywords({
            data: {
                account_id: accountId,
                keywords: groupedByEan
            },
            callback: (data: ResponseData) => {
                setImporting(false);
                if (data.success) {
                    onSuccess();
                    const failedCount =
                        data.errors && typeof data.errors === 'object' ? Object.keys(data.errors).length : 0;
                    const actuallyImported =
                        typeof data.imported_count === 'number'
                            ? data.imported_count
                            : Math.max(0, totalKeywords - failedCount);
                    showSnackbar(
                        t('searchRankings.importKeywords.snackbarImported', '{{total}} keywords geïmporteerd', {
                            total: actuallyImported
                        })
                    );
                    if (failedCount > 0) {
                        showSnackbar(
                            t('searchRankings.importKeywords.snackbarFailed', '{{total}} keywords mislukt', {
                                total: failedCount
                            })
                        );
                    }
                } else {
                    const message =
                        typeof data.message === 'string' && data.message.length
                            ? data.message
                            : t('searchRankings.importKeywords.snackbarFailedAll', 'Importeren is mislukt');
                    showSnackbar(message);
                }
            }
        });
    }, [eanCount, parsedRows, accountId, groupedByEan, onSuccess, t]);

    const handleClose = useCallback(() => {
        setPasteText('');
        setImportFile(null);
        setFileContent('');
        onClose();
    }, [onClose]);

    const handlePasteChange = useCallback((value: string) => {
        setPasteText(value);
    }, []);

    const eanEntries = Object.entries(groupedByEan);
    const previewEntries = eanEntries.slice(0, MAX_PREVIEW_EANS);
    const remainingEans = eanEntries.length - MAX_PREVIEW_EANS;

    return (
        <BluemateDialog
            active={active}
            onClose={handleClose}
            title={t('searchRankings.importKeywords.title', 'Keywords importeren')}
            onConfirm={handleImport}
            confirmLabel={
                importing
                    ? t('searchRankings.importKeywords.busy', 'Bezig...')
                    : t('searchRankings.importKeywords.import', 'Importeren')
            }
            confirmDisabled={parsedRows.length === 0 || importing}
            className='import-keywords-popup'
        >
            <div className='import-content'>
                {/* Mode Toggle */}
                <div className='mode-toggle'>
                    <button className={mode === 'paste' ? 'active' : ''} onClick={() => setMode('paste')}>
                        {t('searchRankings.importKeywords.paste', 'Plakken')}
                    </button>
                    <button className={mode === 'file' ? 'active' : ''} onClick={() => setMode('file')}>
                        {t('searchRankings.importKeywords.uploadFile', 'Bestand uploaden')}
                    </button>
                </div>

                {/* Collapsible help */}
                <button className='help-toggle'>
                    <Icon icon='mdi:help-circle-outline' style={{fontSize: '0.875rem'}} />
                    <span>{t('searchRankings.importKeywords.howItWorks', 'Hoe werkt het?')}</span>
                </button>

                <div className='help-content'>
                    {mode === 'paste' ? (
                        <>
                            <p>{t('searchRankings.importKeywords.useThisFormat', 'Gebruik het volgende formaat:')}</p>
                            <div className='help-formats'>
                                <div className='help-format'>
                                    <span className='format-label'>
                                        {t('searchRankings.importKeywords.withSemicolon', 'Met puntkomma')}
                                    </span>
                                    <span className='format-example'>
                                        9781234567890;draadloze oordopjes
                                        <br />
                                        9780987654321; usb-c kabel
                                    </span>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <p>
                                {t(
                                    'searchRankings.importKeywords.uploadCsvTxt',
                                    'Upload een CSV- of TXT-bestand met puntkomma-gescheiden kolommen:'
                                )}
                            </p>
                            <div className='help-formats'>
                                <div className='help-format'>
                                    <span className='format-label'>
                                        {t(
                                            'searchRankings.importKeywords.csvFormat',
                                            'CSV-formaat (kolom EAN ; kolom keyword)'
                                        )}
                                    </span>
                                    <span className='format-example'>
                                        EAN;keyword
                                        <br />
                                        9781234567890;draadloze oordopjes
                                        <br />
                                        9780987654321;usb-c kabel
                                    </span>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Paste mode */}
                {mode === 'paste' && (
                    <div className='paste-section'>
                        <label className='input-label'>
                            {t('searchRankings.importKeywords.importLabel', 'Keywords importeren:')}
                        </label>
                        <BluemateInput
                            id='import-keywords-paste'
                            value={pasteText}
                            onChange={handlePasteChange}
                            placeholder=''
                            area
                            disabled={importing}
                            fullWidth
                        />
                    </div>
                )}

                {/* File mode */}
                {mode === 'file' && (
                    <div className='file-upload-section'>
                        <label className='input-label'>
                            {t('searchRankings.importKeywords.csvFile', 'CSV-bestand')}
                        </label>
                        <div
                            className='file-drop-zone'
                            onDragOver={e => e.preventDefault()}
                            onDrop={handleFileDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <input
                                ref={fileInputRef}
                                type='file'
                                accept='.csv,.txt'
                                onChange={handleFileInputChange}
                                style={{display: 'none'}}
                            />
                            {!importFile ? (
                                <>
                                    <Icon
                                        icon='mdi:file-upload-outline'
                                        style={{fontSize: '1.5rem', color: '#9CA3AF'}}
                                    />
                                    <span className='drop-zone-text'>
                                        {t(
                                            'searchRankings.importKeywords.dropZoneText',
                                            'Klik om een bestand te selecteren of sleep het hierheen'
                                        )}
                                    </span>
                                    <span className='drop-zone-hint'>
                                        {t(
                                            'searchRankings.importKeywords.dropZoneHint',
                                            'Formaat: EAN;keyword per regel'
                                        )}
                                    </span>
                                </>
                            ) : (
                                <div className='file-info'>
                                    <Icon
                                        icon='mdi:file-document-outline'
                                        style={{fontSize: '1.25rem', color: '#2568EF'}}
                                    />
                                    <span className='file-name'>{importFile.name}</span>
                                    <button
                                        className='file-remove'
                                        onClick={e => {
                                            e.stopPropagation();
                                            handleFileChange(null);
                                        }}
                                    >
                                        <Icon icon='mdi:close' style={{fontSize: '0.875rem'}} />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Parsed preview grouped by EAN */}
                {parsedRows.length > 0 && !importing && (
                    <div className='parsed-preview'>
                        <span className='preview-label'>
                            {t(
                                'searchRankings.importKeywords.previewLabel',
                                '{{keywordCount}} keyword(s) voor {{eanCount}} product(en)',
                                {
                                    keywordCount: parsedRows.length,
                                    eanCount
                                }
                            )}
                        </span>
                        <div className='preview-groups'>
                            {previewEntries.map(([ean, keywords]) => (
                                <div key={ean} className='preview-group'>
                                    <span className='ean-badge'>{ean}</span>
                                    <div className='keyword-list'>
                                        {keywords.slice(0, 5).map((kw, i) => (
                                            <span key={i} className='keyword-tag'>
                                                {kw}
                                            </span>
                                        ))}
                                        {keywords.length > 5 && (
                                            <span className='keyword-more'>+{keywords.length - 5}</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {remainingEans > 0 && (
                                <div className='more-text'>
                                    {t(
                                        'searchRankings.importKeywords.moreProducts',
                                        '...en {{count}} andere product(en)',
                                        {count: remainingEans}
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Progress */}
                {importing && (
                    <div className='progress-section'>
                        <div className='progress-text'>
                            {t('searchRankings.importKeywords.progressText', 'Importeren...')}
                        </div>
                    </div>
                )}
            </div>
        </BluemateDialog>
    );
}
